import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { Login } from '@/pages/Login';
import { Signup } from '@/pages/Signup';
import { Onboarding } from '@/pages/Onboarding';
import { PaymentSetup } from '@/pages/PaymentSetup';
import { VerifyEmail } from '@/pages/VerifyEmail';
import { ForgotPassword } from '@/pages/ForgotPassword';
import { ResetPassword } from '@/pages/ResetPassword';
import { Home } from '@/pages/Home';
import { Account } from '@/pages/Account';
import { Settings } from '@/pages/Settings';
import { Campaign } from '@/pages/Campaign';
import { MetaCampaign } from '@/pages/MetaCampaign';
import { MediaLibrary } from '@/pages/MediaLibrary';
import { CreativeStudio } from '@/pages/CreativeStudio';
import { TestWeek3 } from '@/pages/TestWeek3';
import { TestWeek3Simple } from '@/pages/TestWeek3Simple';
import { FlowerButtonDemo } from '@/pages/FlowerButtonDemo';
import { FlowerButtonTest } from '@/pages/FlowerButtonTest';
import { TestImagePicker } from '@/pages/TestImagePicker';
import { Terms } from '@/pages/Terms';
import { Privacy } from '@/pages/Privacy';
import { MetaDataDeletion } from '@/pages/Legal/MetaDataDeletion';
import { MetaDeletionStatus } from '@/pages/Legal/MetaDeletionStatus';
import { MetaOAuthCallback } from '@/pages/MetaOAuthCallback';
import { NotFound } from '@/pages/NotFound';
import { ROUTES } from '@/config/constants';

export const router = createBrowserRouter([
  {
    path: ROUTES.LOGIN,
    element: <Login />,
  },
  {
    path: '/signup',
    element: <Signup />,
  },
  {
    path: '/onboarding',
    element: <Onboarding />,
  },
  {
    path: '/payment-setup',
    element: <PaymentSetup />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmail />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
  {
    path: '/terms',
    element: <Terms />,
  },
  {
    path: '/privacy',
    element: <Privacy />,
  },
  {
    // Public — reviewers and users reach this without a Flouri account.
    // Documents the Meta data deletion process for App Review §2.8.
    path: '/legal/meta-data-deletion',
    element: <MetaDataDeletion />,
  },
  {
    // Public — backs the URL Meta's data-deletion callback returns.
    // Must remain reachable for ≥30 days per Meta's contract.
    path: '/legal/meta-deletion-status',
    element: <MetaDeletionStatus />,
  },
  {
    // Meta OAuth round-trip target. Top-level (outside Layout/ProtectedRoute)
    // because the backend redirects here on return from Meta — but the page
    // itself uses useAuth() to re-validate the session and gate access.
    path: '/oauth/callback/meta',
    element: <MetaOAuthCallback />,
  },
  {
    path: '/test-week3-direct',
    element: <TestWeek3 />,
  },
  {
    path: '/test-simple',
    element: <TestWeek3Simple />,
  },
  {
    path: '/hello',
    element: <div>Hello World - React is Working!</div>,
  },
  {
    path: '/flower-demo',
    element: <FlowerButtonDemo />,
  },
  {
    path: '/flower-test',
    element: <FlowerButtonTest />,
  },
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        path: '',
        element: <ProtectedRoute />,
        children: [
          {
            // `/` is the public landing page served by FastAPI's @app.get("/")
            // in src/api/main.py:134. Any client-side nav to `/` from within
            // the React app (rare after the dashboard moved to /dashboard)
            // redirects authed users to /dashboard; unauthed users get the
            // ProtectedRoute → /login bounce, which is fine since the only
            // surfaces that should land an unauthed user at `/` (Header /
            // LegalPage / NotFound) use hard-reload `<a href="/">` instead.
            index: true,
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: 'dashboard',
            element: <Home />,
          },
          {
            path: 'account',
            element: <Account />,
          },
          {
            path: 'settings',
            element: <Settings />,
          },
          {
            path: 'media-library',
            element: <MediaLibrary />,
          },
          {
            path: 'creative-studio',
            element: <CreativeStudio />,
          },
          {
            // Legacy alias — kept so existing internal links and
            // bookmarks don't 404. New navigation prefers
            // `/google-campaign/:id` for symmetry with `/meta-campaign/:id`.
            path: 'campaign/:id',
            element: <Campaign />,
          },
          {
            path: 'google-campaign/:id',
            element: <Campaign />,
          },
          {
            path: 'meta-campaign/:id',
            element: <MetaCampaign />,
          },
          {
            path: 'test-week3',
            element: <TestWeek3 />,
          },
          {
            path: 'test-image-picker',
            element: <TestImagePicker />,
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.NOT_FOUND,
    element: <NotFound />,
  },
]);